<template>
  <div class="roles-container">
    <!-- Page Header -->
    <div class="page-header">
      <h1>User Permissions</h1>
      <div class="header-actions">
        <button @click="showCreateRoleForm = true" class="btn btn-primary">
          <i class="icon-plus"></i> Create Role
        </button>
      </div>
    </div>

    <div v-if="error" class="error-message">
      {{ error }}
    </div>
    <div v-if="success" class="success-message">
      {{ success }}
    </div>

    <!-- Create/Edit Role Form -->
    <div v-if="showCreateRoleForm" class="form-panel">
      <div class="panel-header">
        <h3>{{ roleForm.isEditing ? 'Edit Role' : 'Create New Role' }}</h3>
        <button @click="cancelRoleForm" class="btn-close">
          <i class="icon-close"></i>
        </button>
      </div>
      
      <form @submit.prevent="createRole" class="role-form">
        <div class="form-group">
          <label>Role Name *</label>
          <input
            v-model="roleForm.name"
            type="text"
            placeholder="Enter role name"
            required
          >
        </div>
        <div class="form-group">
          <label>Description</label>
          <textarea
            v-model="roleForm.description"
            placeholder="Role description"
            rows="3"
          ></textarea>
        </div>
        
        <div class="permissions-section">
          <h4>Permissions</h4>
          <div class="permissions-grid">
            <div v-for="permission in availablePermissions" :key="permission.id" class="permission-item">
              <input 
                type="checkbox" 
                v-model="roleForm.permissions"
                :value="permission.id"
                :id="`permission-${permission.id}`"
              >
              <label :for="`permission-${permission.id}`" class="permission-content">
                <span class="permission-name">{{ permission.name }}</span>
                <span class="permission-desc">{{ permission.description }}</span>
              </label>
            </div>
          </div>
        </div>
        
        <div class="form-actions">
          <button type="button" @click="cancelRoleForm" class="btn btn-secondary">
            Cancel
          </button>
          <button type="submit" class="btn btn-primary">
            {{ roleForm.isEditing ? 'Update Role' : 'Create Role' }}
          </button>
        </div>
      </form>
    </div>

    <!-- Roles List -->
    <div class="roles-section">
      <div v-if="roles.length === 0" class="empty-state">
        <i class="icon-shield"></i>
        <h4>No Roles Found</h4>
        <p>Create your first role to get started with permission management.</p>
      </div>
      
      <div v-else class="roles-grid">
        <div 
          v-for="role in roles" 
          :key="role.id" 
          class="role-card"
          :class="{ 'system': role.is_system }"
        >
          <div class="role-header">
            <h4>{{ role.name }}</h4>
            <div v-if="role.is_system" class="system-badge">System</div>
          </div>
          <p class="role-description">{{ role.description || 'No description provided.' }}</p>
          <div class="role-stats">
            <span class="user-count">{{ getUserCount(role.id) }} users</span>
            <span class="permission-count">{{ role.permissions?.length || 0 }} permissions</span>
          </div>
          <div v-if="!role.is_system" class="role-actions">
            <button @click="editRole(role)" class="btn-icon" title="Edit">
              <i class="icon-edit"></i>
            </button>
            <button v-if="store.getters['auth/isSuperAdmin']" @click="deleteRole(role.id)" class="btn-icon danger" title="Delete">
              <i class="icon-trash"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.roles-container {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f8f9fa;
  overflow: hidden;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
  background: white;
  border-bottom: 1px solid #e9ecef;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.page-header h1 {
  margin: 0;
  color: #2c3e50;
  font-size: 2rem;
  font-weight: 600;
}

.header-actions {
  display: flex;
  gap: 0.75rem;
}

.error-message,
.success-message {
  padding: 0.75rem 2rem;
  margin: 0;
  font-weight: 500;
}

.error-message {
  background: #f8d7da;
  color: #721c24;
  border-bottom: 1px solid #f1aeb5;
}

.success-message {
  background: #d1e7dd;
  color: #0f5132;
  border-bottom: 1px solid #a3cfbb;
}

.form-panel {
  background: white;
  border-bottom: 1px solid #e9ecef;
  padding: 2rem;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e9ecef;
}

.panel-header h3 {
  margin: 0;
  color: #2c3e50;
  font-size: 1.25rem;
  font-weight: 600;
}

.btn-close {
  width: 32px;
  height: 32px;
  border: none;
  background: #f8f9fa;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  transition: all 0.2s ease;
}

.btn-close:hover {
  background: #e9ecef;
  color: #495057;
}

.role-form {
  max-width: 800px;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #2c3e50;
  font-size: 0.9rem;
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  font-size: 0.95rem;
  transition: border-color 0.2s ease;
}

.form-group input:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #6f42c1;
  box-shadow: 0 0 0 3px rgba(111, 66, 193, 0.1);
}

.permissions-section {
  margin: 2rem 0;
}

.permissions-section h4 {
  margin: 0 0 1rem;
  color: #2c3e50;
  font-size: 1.1rem;
  font-weight: 600;
}

.permissions-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1rem;
  padding: 1.5rem;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.permission-item {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}

.permission-item input[type="checkbox"] {
  width: auto;
  margin: 0;
  margin-top: 0.125rem;
  flex-shrink: 0;
}

.permission-content {
  flex: 1;
  cursor: pointer;
  display: block;
}

.permission-name {
  display: block;
  font-weight: 600;
  color: #2c3e50;
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
}

.permission-desc {
  display: block;
  color: #6c757d;
  font-size: 0.85rem;
  line-height: 1.4;
}

.form-actions {
  display: flex;
  gap: 0.75rem;
  padding-top: 2rem;
  border-top: 1px solid #e9ecef;
}

.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-primary {
  background: #6f42c1;
  color: white;
}

.btn-primary:hover {
  background: #5a2d91;
}

.btn-secondary {
  background: #f8f9fa;
  color: #6c757d;
  border: 1px solid #dee2e6;
}

.btn-secondary:hover {
  background: #e9ecef;
  border-color: #adb5bd;
}

.roles-section {
  flex: 1;
  overflow-y: auto;
  padding: 2rem;
}

.empty-state {
  text-align: center;
  color: #6c757d;
  padding: 4rem 2rem;
}

.empty-state i {
  font-size: 5rem;
  margin-bottom: 1.5rem;
  opacity: 0.3;
  color: #6f42c1;
}

.empty-state h4 {
  margin: 0 0 0.5rem;
  color: #495057;
  font-size: 1.25rem;
}

.empty-state p {
  margin: 0;
  font-size: 0.95rem;
}

.roles-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
}

.role-card {
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 1.5rem;
  transition: all 0.2s ease;
  position: relative;
}

.role-card:hover {
  border-color: #6f42c1;
  box-shadow: 0 4px 12px rgba(111, 66, 193, 0.1);
}

.role-card.system {
  border-color: #ffc107;
  background: linear-gradient(135deg, #fff8e1 0%, #ffffff 100%);
}

.role-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
}

.role-header h4 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #2c3e50;
}

.system-badge {
  background: #ffc107;
  color: #212529;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
}

.role-description {
  margin: 0 0 1rem;
  color: #6c757d;
  font-size: 0.9rem;
  line-height: 1.5;
  min-height: 2.7em;
}

.role-stats {
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 0.85rem;
  color: #6c757d;
  margin-bottom: 1rem;
}

.user-count,
.permission-count {
  background: #f8f9fa;
  color: #495057;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-weight: 500;
}

.role-actions {
  position: absolute;
  top: 1rem;
  right: 1rem;
  display: flex;
  gap: 0.5rem;
}

.btn-icon {
  width: 32px;
  height: 32px;
  border: 1px solid #dee2e6;
  background: white;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6c757d;
  transition: all 0.2s ease;
}

.btn-icon:hover {
  border-color: #6f42c1;
  color: #6f42c1;
}

.btn-icon.danger:hover {
  border-color: #dc3545;
  color: #dc3545;
  background: #fff5f5;
}

/* Icons */
.icon-plus:before { content: '➕'; }
.icon-close:before { content: '✕'; }
.icon-shield:before { content: '🛡️'; }
.icon-edit:before { content: '✏️'; }
.icon-trash:before { content: '🗑️'; }

/* Responsive */
@media (max-width: 768px) {
  .permissions-grid {
    grid-template-columns: 1fr;
  }
  
  .roles-grid {
    grid-template-columns: 1fr;
  }

  .role-actions {
    position: relative;
    top: auto;
    right: auto;
    justify-content: flex-end;
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid #e9ecef;
  }
}
</style>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import { useStore } from 'vuex'

export default {
  name: 'RolesView',
  setup() {
    const store = useStore()
    
    // Reactive state
    const error = ref('')
    const success = ref('')
    const showCreateRoleForm = ref(false)

    // Role form
    const roleForm = reactive({
      id: null,
      name: '',
      description: '',
      permissions: [],
      isEditing: false
    })

    // Available permissions
    const availablePermissions = [
      { id: 'read_models', name: 'Read Models', description: 'View LLM models' },
      { id: 'write_models', name: 'Write Models', description: 'Create and edit LLM models' },
      { id: 'delete_models', name: 'Delete Models', description: 'Delete LLM models' },
      { id: 'read_agents', name: 'Read Agents', description: 'View agents' },
      { id: 'write_agents', name: 'Write Agents', description: 'Create and edit agents' },
      { id: 'delete_agents', name: 'Delete Agents', description: 'Delete agents' },
      { id: 'read_kb', name: 'Read Knowledge Base', description: 'View knowledge bases' },
      { id: 'write_kb', name: 'Write Knowledge Base', description: 'Create and edit knowledge bases' },
      { id: 'delete_kb', name: 'Delete Knowledge Base', description: 'Delete knowledge bases' },
      { id: 'manage_users', name: 'Manage Users', description: 'Invite and manage users' },
      { id: 'manage_roles', name: 'Manage Roles', description: 'Create and edit roles' },
      { id: 'manage_org', name: 'Manage Organization', description: 'Edit organization settings' }
    ]

    // Computed properties
    const roles = computed(() => store.state.roles?.items || [])
    
    const users = computed(() => {
      const rawUsers = store.state.users?.items || []
      return Array.isArray(rawUsers) ? rawUsers.filter(user => 
        user && 
        typeof user === 'object' &&
        (user.id || user._id) && 
        (user.email || user.name)
      ) : []
    })

    // Methods
    const fetchRoles = async () => {
      try {
        await store.dispatch('roles/fetchAll')
      } catch (err) {
        console.error('Error fetching roles:', err)
        error.value = 'Failed to load roles'
        setTimeout(() => error.value = '', 3000)
      }
    }

    const fetchUsers = async () => {
      try {
        await store.dispatch('users/fetchAll')
      } catch (err) {
        console.error('Error fetching users:', err)
      }
    }

    const createRole = async () => {
      try {
        if (roleForm.isEditing) {
          // Update existing role
          await store.dispatch('roles/update', {
            id: roleForm.id,
            updates: {
              name: roleForm.name,
              description: roleForm.description,
              permissions: roleForm.permissions
            }
          })
          success.value = 'Role updated successfully!'
        } else {
          // Create new role
          await store.dispatch('roles/create', {
            name: roleForm.name,
            description: roleForm.description,
            permissions: roleForm.permissions
          })
          success.value = 'Role created successfully!'
        }
        
        setTimeout(() => success.value = '', 3000)
        
        // Reset form
        cancelRoleForm()
        
        await fetchRoles()
      } catch (err) {
        console.error('Error with role operation:', err)
        error.value = roleForm.isEditing ? 'Failed to update role' : 'Failed to create role'
        setTimeout(() => error.value = '', 3000)
      }
    }

    const editRole = (role) => {
      // Populate form with role data for editing
      roleForm.id = role.id
      roleForm.name = role.name
      roleForm.description = role.description || ''
      roleForm.permissions = role.permissions || []
      roleForm.isEditing = true
      showCreateRoleForm.value = true
    }

    const deleteRole = async (roleId) => {
      if (!confirm('Are you sure you want to delete this role?')) return
      
      try {
        await store.dispatch('roles/delete', roleId)
        success.value = 'Role deleted successfully!'
        setTimeout(() => success.value = '', 3000)
        await fetchRoles()
      } catch (err) {
        console.error('Error deleting role:', err)
        error.value = 'Failed to delete role'
        setTimeout(() => error.value = '', 3000)
      }
    }

    const cancelRoleForm = () => {
      roleForm.id = null
      roleForm.name = ''
      roleForm.description = ''
      roleForm.permissions = []
      roleForm.isEditing = false
      showCreateRoleForm.value = false
    }

    const getUserCount = (roleId) => {
      return users.value.filter(user => user.role_id === roleId).length
    }

    // Lifecycle
    onMounted(async () => {
      try {
        await Promise.all([
          fetchRoles(),
          fetchUsers()
        ])
      } catch (error) {
        console.error('Error loading data:', error)
        error.value = 'Failed to load data'
        setTimeout(() => error.value = '', 3000)
      }
    })

    return {
      // Store
      store,
      
      // State
      error,
      success,
      showCreateRoleForm,
      roleForm,
      availablePermissions,
      
      // Computed
      roles,
      users,
      
      // Methods
      createRole,
      editRole,
      deleteRole,
      cancelRoleForm,
      getUserCount
    }
  }
}
</script>

